import { useState } from 'react';
import { Carousel, Col, Container, Image, Row } from 'react-bootstrap';
import { PageMeta } from '../../../components/meta/PageMeta';
import { StickySubNav } from '../../../components/nav/StickySubNav';
import { BannerHeader } from '../../user/BannerHeader';
import { useGetGalleryQuery } from '../../../services/endpoints/website/gallery';
import { getPartition } from '../../../utils/utils';
import Dots from '../../../assets/images/sfc-dots.png';
import familyImg01 from '../../../assets/images/privateEvents/private-family-cooking-class_sticky-fingers-cooking_01.jpg';
import familyImg from '../../../assets/images/privateEvents/private-family-cooking-class_sticky-fingers-cooking.jpg';
import './PrivateEvents.css';

const NAV_ITEMS = [
  { label: 'Online Friends & Family Classes', id: 'online-family-cooking-classes' },
  { label: 'Testimonials', id: 'family-cooking-classes-testimonials' },
  { label: 'Gallery', id: 'family-cooking-classes-gallery' },
];

const TESTIMONIALS = [
  { quote: "Virtual cooking classes with cousins and grandparents across the country! We love Sticky Fingers!", author: "Michelle B., Texas" },
  { quote: "These classes have been a great way to connect with our family in Texas. Your Chef Instructors are amazing!", author: "Green Family, Florida" },
  { quote: "Our family ranged in age from seven to 73 and we all had a blast cooking virtually together.", author: "Sadie, Washington" },
  { quote: "The instructor was clear, fun, and very patient. We felt like the class was designed especially for us!", author: "Leah P." },
  { quote: "I now have a 5-year-old obsessed with making Hungarian goulash and ice cream!", author: "Kate M., New York" },
];

const FamilyCookingClasses: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const { data: galleryData } = useGetGalleryQuery(4);

  return (
      <>
        <PageMeta
            title="Online Family Cooking Classes - Sticky Fingers Cooking"
            description="Connect with loved ones near and far with Sticky Fingers Cooking private family cooking classes! Fun, interactive online and in-person cooking classes for all ages."
        />
        <BannerHeader title="Friends & Family Cooking Classes" />

        <StickySubNav items={NAV_ITEMS} />

        {/* Main Section */}
        <div id="online-family-cooking-classes" className="section-events-child">
          <Container>
            <div className="container-flex">
              <div className="div-block-events-large">
                <h2>Private, Online Cooking Classes</h2>
                <Image className="w-100 dots-divider my-2" src={Dots} />
                <p>Looking for a fun, unique, and delicious way to connect with loved ones from afar? Cook up some fun together VIRTUALLY while Cultivating Cool'inary Curiosity in Kids®!</p>
                <p>Sticky Fingers Cooking offers <strong>one-hour private, online cooking classes</strong> for small groups of family or friends. Have a virtual family reunion, celebrate a birthday, host a playdate or a weekly cooking class, or enjoy breakfast with Grandma!</p>
                <p>You pick the date, time and one of our delicious, kid-approved recipes! We provide a specially trained chef and all the fun! Some of the hundreds of recipes to choose from include:</p>
                <ul className="w-list-unstyled">
                  <li>Ooey-Gooey Cheesy Pizza Bombs + Caprese Salad-on-a-Stick + Kid-made Basil Cream Sodas</li>
                  <li>Magical Middle Eastern Shakshuka Poached Eggs + Pronto Pita Bread + Pineapple Orange Spritzers</li>
                  <li>Spanish Empanada de Manzana (apple pies) + Sorbete de Manzana</li>
                  <li>Mini Mahogany Cakes + Cheery Cherry Chocolate Drizzle + Iced Cherry Vanilla Spritzers</li>
                  <li>Lucky Lo Mein Noodles + Crazy Delicious Caramelized Cabbage + Chinese Sweet Soy Splash</li>
                  <li>Chef Shepherd's Pie + Fresh Spinach Salad + Emerald Ranch Dressing + Lucky Limeade</li>
                  <li>Positively Pumpkin Gnocchi + Whipped Sage Butter + Pumpkin Pie Shakes</li>
                  <li>Cool Chocolate Whoopie Pies + Peppermint Cream + Minty Hot Cocoa</li>
                </ul>
                <p>Looking for a particular recipe? Craving a type of cuisine? Just ask us! We accommodate ANY and ALL dietary restrictions or allergies. <a href="/contact">Contact us for pricing and more details!</a></p>
                <h3>Swag Bags</h3>
                <p>Want to make your private group class EGGS-tra special? Let us put together a custom swag bag for your group. We can include:</p>
                <ul className="w-list-unstyled">
                  <li>Chef hats</li>
                  <li>Mugs</li>
                  <li>Kid-friendly learning knives</li>
                  <li>Embroidered adult &amp; kid aprons</li>
                  <li>Assorted themed, holiday, and/or other celebratory items for the entire family</li>
                </ul>
                <a href="/contact" className="btn-events">Book Your Event</a>
              </div>
              <div className="div-block-events-small">
                <div className="div-image">
                  <img src={familyImg01} alt="Boy who just finished a Sticky Fingers private online cooking class" className="image-events" />
                </div>
                <div className="div-image">
                  <img src={familyImg} alt="Family enjoying a Sticky Fingers private online cooking class" className="image-events rotate-left" />
                </div>
              </div>
            </div>
          </Container>
        </div>

        {/* Testimonials */}
        <section id="family-cooking-classes-testimonials" className="section-testimonial-slider">
          <Container>
            <Row>
              <Col lg={7}>
                <Carousel
                    controls={false}
                    indicators={false}
                    interval={4500}
                    activeIndex={activeIndex}
                    onSelect={(i) => setActiveIndex(i)}
                >
                  {TESTIMONIALS.map((t, i) => (
                      <Carousel.Item key={i}>
                        <div className="text-quote">"{t.quote}"</div>
                        <div className="subheader white">{t.author}</div>
                      </Carousel.Item>
                  ))}
                </Carousel>
              </Col>
            </Row>
            <Row>
              <Col lg={7}>
                <div className="testimonial-dots">
                  {TESTIMONIALS.map((_, i) => (
                      <button
                          key={i}
                          className={`testimonial-dot${activeIndex === i ? ' active' : ''}`}
                          onClick={() => setActiveIndex(i)}
                      />
                  ))}
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Gallery */}
        {galleryData?.gallery?.pictures && galleryData.gallery.pictures.length > 0 && (
            <div id="family-cooking-classes-gallery" className="section-events-child">
              <Container>
                <div className="photo-grid">
                  {galleryData.gallery.pictures.map((pic, i) => (
                      <div key={i} className="photo-grid-item">
                        <img
                            src={`${pic.s3_base_url}/${getPartition(pic.id)}/original/${pic.image_file_name}`}
                            alt={pic.name ?? `Family cooking class ${i + 1}`}
                        />
                      </div>
                  ))}
                </div>
              </Container>
            </div>
        )}
      </>
  );
};

export default FamilyCookingClasses;